// @ts-check

import { createContext, useContext, useEffect, useMemo, useState } from "react";
import { useLocation } from "react-router-dom";
import { startOfDay, subDays, endOfDay } from "date-fns";
import { keepPreviousData } from "@tanstack/react-query";

import { calculateCurrentDatePeriodType } from "components/DateRangePicker/util";

import { useAppQuery } from "hooks/use-app-query";
import { useDebounce } from "hooks/use-debounce";
import { useSearchParam } from "hooks/use-search-param";

import { ORDERS_PAGE_SIZE } from "utils/orders";
import { UTM_SETS_PAGE_SIZE } from "utils/utm-sets";
import { AD_CAMPAIGNS_PAGE_SIZE } from "utils/ad-campaigns";

const GlobalContext = createContext({});

const PLACEHOLDER_TOP_METRICS = {
  utm_source: {
    data: [],
    totalCount: 0,
  },
  utm_medium: {
    data: [],
    totalCount: 0,
  },
  utm_campaign: {
    data: [],
    totalCount: 0,
  },
  utm_content: {
    data: [],
    totalCount: 0,
  },
  utm_term: {
    data: [],
    totalCount: 0,
  },
};

export const GlobalProvider = ({ children }) => {
  const [dates, setDates] = useState(null);
  const [datePeriodType, setDatePeriodType] = useState("lastSevenDays");

  const [includeNoSourceGraphs, setIncludeNoSourceGraphs] = useState(false);

  // orders
  const [ordersPage, setOrdersPage] = useState(1);
  const [ordersQueryTerm, setOrdersQueryTerm] = useState("");
  const debouncedOrdersQueryTerm = useDebounce(ordersQueryTerm, 500);
  const [ordersSortSelected, setOrdersSortSelected] = useState(["date desc"]);

  // utm sets
  const [utmSetsPage, setUtmSetsPage] = useState(1);
  const [utmSetsQueryTerm, setUtmSetsQueryTerm] = useState("");
  const debouncedUtmSetsQueryTerm = useDebounce(utmSetsQueryTerm, 500);

  // url params
  const { pathname } = useLocation();
  const utmSource = useSearchParam("utm_source");
  const utmMedium = useSearchParam("utm_medium");
  const utmCampaign = useSearchParam("utm_campaign");
  const utmContent = useSearchParam("utm_content");
  const utmTerm = useSearchParam("utm_term");

  // saved utm sets
  const [savedUtmSetsPage, setSavedUtmSetsPage] = useState(1);

  // ad campaigns
  const [adCampaignsPage, setAdCampaignsPage] = useState(1);

  const handleSetDates = ({ start, end }) => {
    setDates({ start, end });
    window.localStorage.setItem(
      "selectedDates",
      JSON.stringify({ start, end })
    );
  };

  const {
    data: store,
    refetch: refetchStore,
    isFetching: isFetchingStore,
    isRefetching: isRefetchingStore,
  } = useAppQuery({
    url: "/api/shop",
    extraQueryKeys: ["store"],
    reactQueryOptions: {
      enabled: dates !== null,
      staleTime: 10 * 60 * 1000,
      placeholderData: keepPreviousData,
    },
  });

  // try load date period from local storage, if not default to 7 days
  useEffect(() => {
    let datesFromLocalStorage = {};
    try {
      datesFromLocalStorage = JSON.parse(window.localStorage.getItem("selectedDates")) || {};
    } catch (e) {
      // do nothing
    }

    const { start, end } = datesFromLocalStorage;

    if (start && end) {
      // if we get some dates from local storage, use them
      setDates({
        start: new Date(start),
        end: new Date(end),
      });
    } else {
      // otherwise, default to previous 7 days
      const dates = {
        start: startOfDay(subDays(new Date(), 6)),
        end: endOfDay(new Date()),
      };

      setDates(dates);
    }
  }, []);

  useEffect(() => {
    if (dates) {
      const currentDatePeriodType = calculateCurrentDatePeriodType(dates);
      setDatePeriodType(currentDatePeriodType);
    }
  }, [dates]);

  const ordersUrl = useMemo(() => {
    const params = {
      page: ordersPage,
      page_size: ORDERS_PAGE_SIZE,
      browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (dates) {
      params.start_date = dates.start.toISOString();
      params.end_date = dates.end.toISOString();
    }

    if (pathname === "/orders") {
      // only add custom params if we're on this page now

      if (utmSource) {
        params.utm_source = utmSource;
      }
      if (utmMedium) {
        params.utm_medium = utmMedium;
      }
      if (utmCampaign) {
        params.utm_campaign = utmCampaign;
      }
      if (utmContent) {
        params.utm_content = utmContent;
      }
      if (utmTerm) {
        params.utm_term = utmTerm;
      }

      if (debouncedUtmSetsQueryTerm) {
        params.search_query = debouncedUtmSetsQueryTerm;
      }
    }

    if (ordersSortSelected.includes("date desc")) {
      params.sort_by = "order_created_at";
      params.sort_order = "desc";
    } else if (ordersSortSelected.includes("date asc")) {
      params.sort_by = "order_created_at";
      params.sort_order = "asc";
    }

    if (debouncedOrdersQueryTerm) {
      params.query = debouncedOrdersQueryTerm;
    }

    return `/api/orders?${new URLSearchParams(params).toString()}`;
  }, [
    dates,
    ordersPage,
    debouncedOrdersQueryTerm,
    pathname,
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    utmTerm,
    ordersSortSelected,
  ]);

  const {
    data: ordersResponse,
    refetch: refetchOrders,
    isPlaceholderData: isPlaceholderOrders,
    isFetching: isFetchingOrders,
  } = useAppQuery({
    url: ordersUrl,
    extraQueryKeys: ["orders"],
    reactQueryOptions: {
      enabled: dates !== null,
      staleTime: 10 * 60 * 1000,
      placeholderData: keepPreviousData,
    },
  });

  const orders = ordersResponse?.orders || [];
  const ordersTotalCount = ordersResponse?.totalCount || 0;

  const utmSetsUrl = useMemo(() => {
    const params = {
      page: utmSetsPage,
      page_size: UTM_SETS_PAGE_SIZE,
      browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (dates) {
      params.start_date = dates.start.toISOString();
      params.end_date = dates.end.toISOString();
    }

    if (pathname === "/utm-sets") {
      // only add custom params if we're on this page now

      if (utmSource) {
        params.utm_source = utmSource;
      }
      if (utmMedium) {
        params.utm_medium = utmMedium;
      }
      if (utmCampaign) {
        params.utm_campaign = utmCampaign;
      }
      if (utmContent) {
        params.utm_content = utmContent;
      }
      if (utmTerm) {
        params.utm_term = utmTerm;
      }

      if (debouncedUtmSetsQueryTerm) {
        params.search_query = debouncedUtmSetsQueryTerm;
      }
    }

    return `/api/utm-sets/computed?${new URLSearchParams(params).toString()}`;
  }, [
    utmSetsPage,
    dates,
    pathname,
    debouncedUtmSetsQueryTerm,
    utmSource,
    utmMedium,
    utmCampaign,
    utmContent,
    utmTerm,
  ]);

  const {
    data: utmSetsResponse,
    refetch: refetchUtmSets,
    isPlaceholderData: isPlaceholderUtmSets,
    isRefetching: isRefetchingUtmSets,
    isFetching: isFetchingUtmSets,
  } = useAppQuery({
    url: utmSetsUrl,
    extraQueryKeys: ["utmSets"],
    reactQueryOptions: {
      enabled: dates !== null,
      staleTime: 10 * 60 * 1000,
      placeholderData: keepPreviousData,
    },
  });

  const utmSets = utmSetsResponse?.utmSets || [];
  const utmSetsTotalCount = utmSetsResponse?.totalCount || 0;

  const savedUtmSetsUrl = useMemo(() => {
    const params = {
      page: savedUtmSetsPage,
      page_size: UTM_SETS_PAGE_SIZE,
    };

    return `/api/utm-sets/saved?${new URLSearchParams(params).toString()}`;
  }, [savedUtmSetsPage, dates, debouncedUtmSetsQueryTerm]);

  const {
    data: savedUtmSetsResponse,
    refetch: refetchSavedUtmSets,
    isPlaceholderData: isPlaceholderSavedUtmSets,
    isRefetching: isRefetchingSavedUtmSets,
    isFetching: isFetchingSavedUtmSets,
  } = useAppQuery({
    url: savedUtmSetsUrl,
    extraQueryKeys: ["savedUtmSets"],
    reactQueryOptions: {
      enabled: dates !== null,
      staleTime: 10 * 60 * 1000,
      placeholderData: keepPreviousData,
    },
  });

  const savedUtmSets = savedUtmSetsResponse?.utmSets || [];
  const savedUtmSetsTotalCount = savedUtmSetsResponse?.totalCount || 0;

  const metricsUrl = useMemo(() => {
    const params = {
      browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
    };

    if (dates) {
      params.start_date = dates.start.toISOString();
      params.end_date = dates.end.toISOString();
    }

    return `/api/v2/metrics?${new URLSearchParams(params).toString()}`;
  }, [dates]);

  const {
    data: metrics,
    refetch: refetchMetrics,
    isPlaceholderData: isPlaceholderMetrics,
    isLoading: isLoadingMetrics,
    isRefetching: isRefetchingMetrics,
  } = useAppQuery({
    url: metricsUrl,
    extraQueryKeys: ["metrics"],
    reactQueryOptions: {
      enabled: dates !== null,
      staleTime: 10 * 60 * 1000,
      placeholderData: {
        salesBySource: {
          data: [],
          error: false,
        },
        salesByDay: {
          data: [],
          error: false,
        },
        totalSales: {
          data: 0,
          error: false,
        },
        revenueBySource: {
          data: [],
          error: false,
        },
        revenueByDay: {
          data: [],
          error: false,
        },
        totalRevenue: {
          data: 0,
          error: false,
        },
        aovByDay: {
          data: [],
          error: false,
        },
        totalAov: {
          data: 0,
          error: false,
        },
        topMetrics: {
          data: PLACEHOLDER_TOP_METRICS,
          error: false,
        },
      },
    },
  });

  const {
    data: orderExportsResponse,
    refetch: refetchOrderExports,
    isPlaceholder: isPlaceholderOrderExports,
    isLoading: isLoadingOrderExports,
    isRefetching: isRefetchingOrderExports,
  } = useAppQuery({
    url: "/api/order-exports",
    extraQueryKeys: ["orderExports"],
    reactQueryOptions: {
      staleTime: 10 * 60 * 1000,
      placeholderData: {
        orderExportJobs: [],
      },
    },
  });

  const orderExports = orderExportsResponse?.data?.orderExportJobs || [];
  const orderExportsLocked = orderExportsResponse?.locked;

  const {
    data: utmSetExportsResponse,
    refetch: refetchUtmSetExports,
    isPlaceholder: isPlaceholderUtmSetExports,
    isLoading: isLoadingUtmSetExports,
    isRefetching: isRefetchingUtmSetExports,
  } = useAppQuery({
    url: "/api/utm-set-exports",
    extraQueryKeys: ["utmSetExports"],
    reactQueryOptions: {
      staleTime: 10 * 60 * 1000,
      placeholderData: {
        orderExportJobs: [],
      },
    },
  });

  const utmSetExports = utmSetExportsResponse?.data?.utmSetExportJobs || [];
  const utmSetExportsLocked = utmSetExportsResponse?.locked;

  const {
    data: savedUtmSetExportsResponse,
    refetch: refetchSavedUtmSetExports,
    isPlaceholder: isPlaceholderSavedUtmSetExports,
    isLoading: isLoadingSavedUtmSetExports,
    isRefetching: isRefetchingSavedUtmSetExports,
  } = useAppQuery({
    url: "/api/saved-utm-set-exports",
    extraQueryKeys: ["savedUtmSetExports"],
    reactQueryOptions: {
      staleTime: 10 * 60 * 1000,
      placeholderData: {
        orderExportJobs: [],
      },
    },
  });

  const savedUtmSetExports = savedUtmSetExportsResponse?.data?.savedUtmSetExportJobs || [];
  const savedUtmSetExportsLocked = savedUtmSetExportsResponse?.locked;

  const locked = Boolean(ordersResponse?.locked);

  const adCampaignsUrl = useMemo(() => {
    const params = {
      browser_timezone: Intl.DateTimeFormat().resolvedOptions().timeZone,
      page: adCampaignsPage,
      page_size: AD_CAMPAIGNS_PAGE_SIZE,
      platform_key: "facebook",
    };

    if (dates) {
      params.start_date = dates.start.toISOString();
      params.end_date = dates.end.toISOString();
    }

    return `/api/ad-campaigns?${new URLSearchParams(params).toString()}`;
  }, [adCampaignsPage, dates]);

  const {
    data: adCampaignsResponse,
    refetch: refetchAdCampaigns,
    isPlaceholderData: isPlaceholderAdCampaigns,
    isFetching: isFetchingAdCampaigns,
  } = useAppQuery({
    url: adCampaignsUrl,
    extraQueryKeys: ["adCampaigns"],
    reactQueryOptions: {
      enabled: dates !== null,
      staleTime: 10 * 60 * 1000,
      placeholderData: keepPreviousData,
    },
  });

  const adCampaigns = adCampaignsResponse?.adCampaigns || [];
  const adCampaignsTotalCount = adCampaignsResponse?.totalCount || 0;

  return (
    <GlobalContext.Provider
      value={{
        includeNoSourceGraphs,
        setIncludeNoSourceGraphs,

        locked,
        store,
        syncing: store?.syncing,
        isFetchingStore,
        isRefetchingStore,
        refetchStore,

        refetchMetrics,
        isPlaceholderMetrics,
        isLoadingMetrics,
        isRefetchingMetrics,
        salesBySource: metrics.salesBySource,
        salesByDay: metrics.salesByDay,
        totalSales: metrics.totalSales,
        revenueBySource: metrics.revenueBySource,
        revenueByDay: metrics.revenueByDay,
        totalRevenue: metrics.totalRevenue,
        aovByDay: metrics.aovByDay,
        totalAov: metrics.totalAov,
        topMetrics: metrics.topMetrics,

        orders,
        isPlaceholderOrders,
        isFetchingOrders,
        refetchOrders,
        ordersPage,
        setOrdersPage,
        ordersTotalCount,
        ordersQueryTerm,
        setOrdersQueryTerm,
        ordersSortSelected,
        setOrdersSortSelected,

        utmSets,
        isPlaceholderUtmSets,
        isFetchingUtmSets,
        isRefetchingUtmSets,
        refetchUtmSets,
        utmSetsPage,
        setUtmSetsPage,
        utmSetsTotalCount,
        utmSetsQueryTerm,
        setUtmSetsQueryTerm,

        savedUtmSets,
        refetchSavedUtmSets,
        isPlaceholderSavedUtmSets,
        isRefetchingSavedUtmSets,
        isFetchingSavedUtmSets,
        savedUtmSetsTotalCount,
        savedUtmSetsPage,
        setSavedUtmSetsPage,

        orderExports,
        orderExportsLocked,
        refetchOrderExports,
        isPlaceholderOrderExports,
        isLoadingOrderExports,
        isRefetchingOrderExports,

        utmSetExports,
        utmSetExportsLocked,
        refetchUtmSetExports,
        isPlaceholderUtmSetExports,
        isLoadingUtmSetExports,
        isRefetchingUtmSetExports,

        savedUtmSetExports,
        savedUtmSetExportsLocked,
        refetchSavedUtmSetExports,
        isPlaceholderSavedUtmSetExports,
        isLoadingSavedUtmSetExports,
        isRefetchingSavedUtmSetExports,

        dates,
        setDates: handleSetDates,
        datePeriodType,

        adCampaigns,
        isPlaceholderAdCampaigns,
        isFetchingAdCampaigns,
        refetchAdCampaigns,
        adCampaignsPage,
        setAdCampaignsPage,
        adCampaignsTotalCount,
      }}
    >
      {children}
    </GlobalContext.Provider>
  );
};

export const useGlobalContext = () => {
  const context = useContext(GlobalContext);

  return context;
};
